import { Paper } from '@mui/material';
import {
  constants,
  DateWrapper,
  FolderList,
  IconTitleUnderline,
} from '@trucktrax/trucktrax-common';
import {
  DriverDto,
  PlantDto,
  PositionDto,
  ProductLine,
  StatusEventDto,
  TicketDto,
} from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  STATUSES_STRING,
  TICKET_STATUS_CLOSED_DRIVER,
  TICKET_STATUS_CLOSED_SYSTEM,
  TICKET_STATUS_OPEN,
} from '../../../../constants/appConstants';
import { fetchDriver } from '../../../../services/driversService';
import getLocations from '../../../../services/geolocationsService';
import { fetchStatusList } from '../../../../services/statusesService';
import { openFailModal } from '../../../../store/actions/errorModalActions';
import { getIdFromUrl } from '../../../../util/appUtil';
import {
  aggFolderList,
  cementFolderList,
  determineWorkflowFolderKey,
  FOLDER_LIST_SOURCE_KEY,
  FolderListSource,
  ProductLineStatusMapKeys,
  readyMixFolderList,
  retrieveProductLineNameDiff,
  WorkFlowProductLines,
} from '../../../../util/statusesUtil';
import {
  effectiveProductLine,
  removePositionsWithDuplicateMinutes,
  StatusFolder,
  statusFolderList,
} from '../../../../util/ticketUtil';
import styles from './RouteInfo.module.css';
import RouteInfoMap from './RouteInfoMap';

const { STATUS_IDS, STATUS_NAMES, STATUS_COLORS } = constants;

const STATUS_WORKFLOW_ORDER_INTERNAL: Record<string, number> = {
  [STATUS_IDS.IN_QUEUE]: 0,
  [STATUS_IDS.TICKETED]: 1,
  [STATUS_IDS.LOADING]: 2,
  [STATUS_IDS.TO_JOB]: 3,
  [STATUS_IDS.ON_JOB]: 4,
  [STATUS_IDS.UNLOAD_START]: 5,
  [STATUS_IDS.UNLOADED]: 6,
  [STATUS_IDS.RETURNING]: 7,
  [STATUS_IDS.IN_YARD]: 8,
};

const STATUS_WORKFLOW_ORDER_EXTERNAL: Record<string, number> = {
  [STATUS_IDS.IN_YARD]: 0,
  [STATUS_IDS.IN_QUEUE]: 1,
  [STATUS_IDS.TICKETED]: 2,
  [STATUS_IDS.LOADING]: 3,
  [STATUS_IDS.TO_JOB]: 4,
  [STATUS_IDS.ON_JOB]: 5,
  [STATUS_IDS.RETURNING]: 6,
};

// Move interfaces to top for better organization
interface FoldersOpenState {
  Ticketed: boolean;
  ToJob: boolean;
  OnJob: boolean;
  Unloaded: boolean;
  Returning: boolean;
  InYard: boolean;
  Unknown: boolean;
}

export interface RouteInfoProps {
  selectedTicket: TicketDto;
  plantList: PlantDto[];
  ticketTimeline: string;
  fetchStatusList: (ticket: TicketDto) => Promise<StatusEventDto[]>;
  selectedProductLine?: ProductLine;
  drivers: DriverDto[];
  /**
   * DEV-3924: per-ticket product line derived from the ticket's origination plant.
   * `undefined` (default) — use the globally selected product line (main-map behavior).
   * `null` — resolution pending/failed: render the ticket details but no status tree.
   * A ProductLine value — build the status tree from it.
   */
  productLineOverride?: ProductLine | null;
  /**
   * DEV-3924: workflow variant to use when the driver's externality cannot be resolved
   * (no ticket driver, or the on-demand fetch fails). Incoming Materials passes true —
   * IM trucks are overwhelmingly external; the main map keeps the internal default.
   */
  unresolvedDriverIsExternal?: boolean;
}

export interface RouteInfoState {
  positions?: {
    data: {
      items: PositionDto[];
    };
  };
  foldersOpen: FoldersOpenState;
  statuses?: StatusFolder[];
  selectedItem: string | null;
  mapCentered: boolean;
  driverClosed: boolean;
  driverIsExternal: boolean;
  routeInfoFolderSourceKey: FolderListSource;
}

interface StatusItemData {
  id: string;
  name: string;
  historical: boolean;
  color?: string;
  position: PositionDto;
}

const StatusUtils = {
  getReadableTimestamp: (timestamp: string | undefined): string =>
    new DateWrapper(timestamp).toString('h:mm a'),

  getStatusColor: (statusId: string): string =>
    STATUS_COLORS[statusId as keyof typeof STATUS_COLORS] || STATUS_COLORS.Unknown,

  getStatusDisplayName: (statusId: string): string =>
    STATUS_NAMES[statusId as keyof typeof STATUS_NAMES] || STATUS_NAMES.Unknown,

  updateCurrentStatusItemHistorical: (
    currentStatus: StatusFolder,
    itemId: string,
    positionStatus: string,
    statusNameMap: Record<string, string | undefined>,
  ): void => {
    if (
      positionStatus === statusNameMap[STATUS_IDS.TO_JOB] ||
      positionStatus === statusNameMap[STATUS_IDS.RETURNING]
    ) {
      currentStatus.itemList.forEach((timestamp: any) => {
        if (timestamp.id === itemId) {
          timestamp.historical = false;
        }
      });
    }
  },

  processStatusList: (statusList: StatusEventDto[], isExternal: boolean): StatusFolder[] =>
    statusList
      ?.map(
        (status): StatusFolder => ({
          name: status.status ?? '',
          time: status.timestamp,
          itemList: [],
          color: '',
          dataTest: '',
          icon: '',
          id: status.id.toString(),
          text: '',
          location: status.location,
        }),
      )
      .filter(status => status.name !== 'DeliveryStart')
      .sort((x, y) => {
        const xTime = x.time ? new DateWrapper(x.time).date.getTime() : Infinity;
        const yTime = y.time ? new DateWrapper(y.time).date.getTime() : Infinity;
        const timeDiff = xTime - yTime;
        if (timeDiff !== 0) return timeDiff;
        const order = isExternal ? STATUS_WORKFLOW_ORDER_EXTERNAL : STATUS_WORKFLOW_ORDER_INTERNAL;
        const workflowDiff = (order[x.name] ?? 99) - (order[y.name] ?? 99);
        if (workflowDiff !== 0) return workflowDiff;
        return x.id.localeCompare(y.id, undefined, { numeric: true, sensitivity: 'base' });
      }) ?? [],

  shouldMarkAsCurrentPosition: (
    positionStatus: string,
    statusNameMap: Record<string, string | undefined>,
    index: number,
    count: number,
    ticketTimeline: string,
  ): boolean =>
    ticketTimeline === 'current' &&
    (positionStatus === statusNameMap[STATUS_IDS.TO_JOB] ||
      positionStatus === statusNameMap[STATUS_IDS.RETURNING]) &&
    index === count - 1,

  getTitle: (icon: string, title: string, value?: string, className?: string): JSX.Element => (
    <div className={className}>
      <div className="flex-vertical-center">
        <i className={cx(icon, styles.markerIcon, 'margin-right-10')} />
        <span className="txt-bold">{title}</span>
      </div>
      <div className={styles.titleValue}>{value}</div>
    </div>
  ),
};

export class RouteInfo extends Component<RouteInfoProps, RouteInfoState> {
  static defaultProps: Partial<RouteInfoProps> = {
    selectedProductLine: undefined,
    productLineOverride: undefined,
    unresolvedDriverIsExternal: false,
  };

  /**
   * The product line the status tree is built from. An explicit `null` override means
   * "unresolved" — never fall back to the selected product line in that case (DEV-3924).
   */
  private get effectiveProductLine(): ProductLine | undefined {
    return effectiveProductLine(this.props.productLineOverride, this.props.selectedProductLine);
  }

  constructor(props: RouteInfoProps) {
    super(props);
    // Resolve what we can synchronously; componentDidMount finishes the job when the
    // driver is missing from the region-scoped list and must be fetched on demand.
    const driverIsExternal =
      this.lookUpDriverIsExternal() ?? props.unresolvedDriverIsExternal === true;
    this.state = {
      foldersOpen: {
        Ticketed: false,
        ToJob: false,
        OnJob: false,
        Unloaded: false,
        Returning: false,
        InYard: false,
        Unknown: false,
      },
      selectedItem: null,
      mapCentered: true,
      driverClosed: false,
      driverIsExternal,
      routeInfoFolderSourceKey: RouteInfo.folderSourceKeyFor(driverIsExternal),
    };
  }

  private static folderSourceKeyFor(driverIsExternal: boolean): FolderListSource {
    return driverIsExternal
      ? FOLDER_LIST_SOURCE_KEY.EXTERNAL_DRIVER_ROUTES
      : FOLDER_LIST_SOURCE_KEY.INTERNAL_DRIVER_ROUTES;
  }

  // Guards the setState calls that follow awaits (driver fetch, status/position fetches)
  // against landing after the ticket view is closed mid-flight.
  private hasUnmounted = false;

  async componentDidMount(): Promise<void> {
    const driverIsExternal = await this.resolveDriverIsExternal();
    if (this.hasUnmounted) {
      return;
    }
    if (driverIsExternal !== this.state.driverIsExternal) {
      this.setState({
        driverIsExternal,
        routeInfoFolderSourceKey: RouteInfo.folderSourceKeyFor(driverIsExternal),
      });
    }
    await this.getRouteStatuses(driverIsExternal);
  }

  componentWillUnmount(): void {
    this.hasUnmounted = true;
  }

  private getRouteStatuses = async (
    driverIsExternal: boolean = this.state.driverIsExternal,
  ): Promise<void> => {
    const { selectedTicket } = this.props;
    const statusList = await this.props.fetchStatusList(selectedTicket);
    if (this.hasUnmounted) {
      return;
    }

    const lastInYardStatus = statusList
      ?.filter(status => status.status === STATUS_IDS.IN_YARD)
      .at(-1);

    this.setState({
      driverClosed: lastInYardStatus?.driverAttested ?? false,
    });

    const sortedStatuses = StatusUtils.processStatusList(statusList, driverIsExternal);
    await this.getPositionsForAllStatuses(sortedStatuses, driverIsExternal);
  };

  /**
   * Externality from the region-scoped drivers list. `undefined` means "not in the list"
   * (e.g. a cross-region GT-Lite driver, or the ticket has no driver at all) — NOT internal;
   * resolveDriverIsExternal then tries an on-demand fetch before applying the default.
   */
  private lookUpDriverIsExternal = (): boolean | undefined => {
    const driverUrl = this.props.selectedTicket?.driver?.url;
    if (!driverUrl) {
      return undefined;
    }
    const match = this.props.drivers.find(source => source.url === driverUrl);
    return match ? match.isExternal === true : undefined;
  };

  private resolveDriverIsExternal = async (): Promise<boolean> => {
    const fromList = this.lookUpDriverIsExternal();
    if (fromList !== undefined) {
      return fromList;
    }

    const { selectedTicket, unresolvedDriverIsExternal } = this.props;
    const driverUrl = selectedTicket?.driver?.url;
    if (driverUrl) {
      try {
        const driver = await fetchDriver(getIdFromUrl(driverUrl), selectedTicket?.region?.url);
        if (driver) {
          return driver.isExternal === true;
        }
      } catch (e) {
        // Unresolvable (e.g. cross-region 403) — fall through to the configured default.
      }
    }
    return unresolvedDriverIsExternal === true;
  };

  private getPositionsForAllStatuses = async (
    sortedStatuses: StatusFolder[],
    driverIsExternal: boolean = this.state.driverIsExternal,
  ): Promise<void> => {
    if (!sortedStatuses.length) {
      this.setState({ positions: { data: { items: [] } }, statuses: sortedStatuses });
      return;
    }

    const { selectedTicket } = this.props;
    const { closedTimestamp } = selectedTicket;

    // Compute the folder source key from the passed-in value rather than reading state,
    // since componentDidMount's setState (batched) may not have landed before this runs.
    const routeInfoFolderSourceKey = RouteInfo.folderSourceKeyFor(driverIsExternal);

    // Single HTTP call for the entire ticket timeline instead of one call per status.
    // Client-side distribution (distributePositionsToFolders) then assigns each ping
    // to the correct folder, which also resolves the grouped-status boundary issue
    // (e.g. Loading + Ticketed both mapping to the "Ticketed" UI folder for Cement/Agg).
    //
    // For closed tickets use closedTimestamp; for open tickets cap at the current time
    // so a long-running ticket doesn't pull an unbounded set of pings going forward.
    const endTime = closedTimestamp ?? new Date().toISOString();
    const allPositions = await getLocations(selectedTicket?.url, sortedStatuses[0].time, endTime);
    if (this.hasUnmounted) {
      return;
    }

    // DEV-3886: bucket first, then condense per-folder. Pre-condensing the flat
    // list discards the ping closest to each status transition when pings arrive
    // faster than once per minute, leaving the new status's folder empty even
    // though a usable ping exists in the data. The returned flat list is the set
    // of survivors after per-folder sampling and feeds both the folder UI and
    // the map so that a folder click always resolves to a marker.
    const survivingPositions = this.distributePositionsToFolders(
      allPositions,
      sortedStatuses,
      routeInfoFolderSourceKey,
    );

    // For status folders that received no pings but carry a GPS location on the status
    // event itself, insert a synthetic pin so the folder is not shown as empty. The pin
    // is also pushed into the surviving positions so it renders on the map — not just the
    // sidebar (DEV-3887: the closing Returning ping has no surviving geolocation for
    // external drivers, so the map relies on the location carried on the status event).
    // Synthetic ids decrement from -1 so multiple fallback pins never collide as map keys
    // (the id is only a uniqueness device, not an ordering signal — the sort below orders).
    // Only statuses the Status History reads from qualify: a folderless status (e.g.
    // Unloaded / UnloadStart for external Cement, tied with Returning) or a grouped one
    // (Loading under Ticketed) would otherwise get a pin that shows on the map — e.g. as
    // "Unload End" — while never appearing in the Status History.
    let syntheticPinId = 0;
    this.getSidebarStatuses(sortedStatuses, routeInfoFolderSourceKey).forEach(statusFolder => {
      if (!statusFolder.itemList.length && statusFolder.location) {
        syntheticPinId -= 1;
        const item = this.buildSyntheticPin(statusFolder, syntheticPinId, selectedTicket);
        this.addLocationItemToFolder(statusFolder, item, 0, 1, routeInfoFolderSourceKey);
        survivingPositions.push(item);
      }
    });

    // Keep the map's position list chronological after appending any synthetic pins.
    survivingPositions.sort((a, b) => DateWrapper.sort(a.timestamp, b.timestamp));

    this.setState({
      positions: { data: { items: survivingPositions } },
      statuses: sortedStatuses,
    });
  };

  /**
   * Assigns each position in the flat sorted list to the StatusFolder whose time window
   * contains it, then resolves to the UI-folder representative so that statusFolderList
   * populates the correct folder.
   *
   * "Representative" = the first StatusFolder in sortedStatuses that maps to a given
   * folder key. Multiple statuses can share a folder key (e.g. Loading + Ticketed →
   * "Ticketed" for Cement/Aggregates); all their positions must land in the first one
   * because that is the entry statusFolderList uses when building the sidebar.
   *
   * Pings are bucketed first and only then condensed (~1 per minute) inside each
   * folder. This is the DEV-3886 fix: global pre-condensing routinely discarded the
   * ping aligned with a status transition when pings came faster than once per
   * minute. Per-folder condensing always preserves the first ping after each
   * transition because that ping is the bucket's earliest entry.
   *
   * Returns the flat list of surviving positions (post-condensing) in chronological
   * order so the caller can feed it to the map and keep folder-click selection in sync.
   */
  /**
   * Builds a synthetic pin for a status folder that carries a GPS location but received
   * no real pings, so the folder still renders a marker. The id is a caller-supplied
   * uniqueness device (negative, to avoid colliding with real position ids); it is not an
   * ordering signal — callers sort by timestamp.
   */
  private buildSyntheticPin = (
    statusFolder: StatusFolder,
    id: number,
    ticket: TicketDto,
  ): PositionDto =>
    ({
      id,
      ticket,
      trackedObject: ticket.truck,
      speed: 0,
      bearing: 0,
      active: true,
      timestamp: statusFolder.time,
      location: statusFolder.location,
      // DEV-3924: synthetic pins route folders via item.productLine — key to the
      // ticket-derived product line, never the main map's selection.
      productLine: this.effectiveProductLine,
    }) as PositionDto;

  private folderKeyOf = (
    statusName: string,
    routeInfoFolderSourceKey: FolderListSource,
  ): string | undefined =>
    determineWorkflowFolderKey(
      this.effectiveProductLine as WorkFlowProductLines,
      statusName,
      routeInfoFolderSourceKey,
    ) as string | undefined;

  /**
   * The statuses whose folder is ACTUALLY rendered for this product line + source, or
   * all statuses when none are (e.g. unresolved product line — no folder list at all).
   * A status can resolve to a folder key that has no rendered folder: e.g. for external
   * Cement Route Info, getCementFolderKey still returns Unloaded/UnloadStart keys, but
   * the external folder list omits those folders. statusFolderList then silently drops
   * anything attached to such a status (DEV-3887: when Unloaded shares the exit timestamp
   * with Returning, the closing ping was bucketed onto the folderless Unloaded status and
   * vanished from the Status History).
   */
  private getRenderedStatuses = (
    sortedStatuses: StatusFolder[],
    routeInfoFolderSourceKey: FolderListSource,
  ): StatusFolder[] => {
    const renderedFolderKeys = this.getRenderedFolderKeys(routeInfoFolderSourceKey);
    const rendered = sortedStatuses.filter(status =>
      renderedFolderKeys.has(this.folderKeyOf(status.name, routeInfoFolderSourceKey)),
    );
    return rendered.length ? rendered : sortedStatuses;
  };

  /**
   * The statuses the Status History actually reads pins from, or all statuses when none
   * are (same fallback as getRenderedStatuses). Stricter than getRenderedStatuses:
   * statusFolderList copies a folder's items only from the status NAMED after the folder
   * key, so a grouped status (e.g. Loading, which maps to the Ticketed folder for Cement /
   * Aggregates) is never read even though its folder is rendered.
   */
  private getSidebarStatuses = (
    sortedStatuses: StatusFolder[],
    routeInfoFolderSourceKey: FolderListSource,
  ): StatusFolder[] => {
    const renderedFolderKeys = this.getRenderedFolderKeys(routeInfoFolderSourceKey);
    const sidebar = sortedStatuses.filter(status => renderedFolderKeys.has(status.name));
    return sidebar.length ? sidebar : sortedStatuses;
  };

  private getRenderedFolderKeys = (
    routeInfoFolderSourceKey: FolderListSource,
  ): Set<string | undefined> =>
    new Set(this.getFolderListOfProductLine(routeInfoFolderSourceKey).map(folder => folder.key));

  private distributePositionsToFolders = (
    positions: PositionDto[],
    sortedStatuses: StatusFolder[],
    routeInfoFolderSourceKey: FolderListSource,
  ): PositionDto[] => {
    const folderKeyOf = (statusName: string): string | undefined =>
      this.folderKeyOf(statusName, routeInfoFolderSourceKey);

    // Restrict bucketing to statuses whose folder is rendered, so a ping at a tied
    // timestamp folds into the nearest visible folder (Returning) instead of vanishing.
    // The backwards-walk and representativeByKey below then run unchanged on this list.
    const renderedOrAll = this.getRenderedStatuses(sortedStatuses, routeInfoFolderSourceKey);

    // Pre-compute: folder key → the StatusFolder whose itemList that folder will render
    // from. statusFolderList copies a folder's items from the status whose NAME matches the
    // folder id (i.e. name === key), so the representative MUST be that status. Prefer the
    // status named after the key; fall back to the first status mapping to the key.
    // (DEV-3886: external Cement staggers Loading 1ms before Ticketed and both map to the
    // "Ticketed" key — without this, Loading became the representative and the Ticketed
    // ping landed on a status with no rendered folder, vanishing from the sidebar.)
    const representativeByKey = new Map<string | undefined, StatusFolder>();
    renderedOrAll.forEach(status => {
      const key = folderKeyOf(status.name);
      const existing = representativeByKey.get(key);
      if (!existing || (status.name === key && existing.name !== key)) {
        representativeByKey.set(key, status);
      }
    });

    // Bucket every ping into its representative folder. Map preserves insertion
    // order, so the first bucket inserted corresponds to the earliest folder
    // that received any pings.
    const bucketByRepresentative = new Map<StatusFolder, PositionDto[]>();
    positions.forEach(position => {
      const positionTime = new DateWrapper(position.timestamp).date.getTime();

      // Last rendered status whose timestamp is ≤ the position's; defaults to the earliest.
      let activeStatus = renderedOrAll[0];
      for (let i = renderedOrAll.length - 1; i >= 0; i--) {
        if (new DateWrapper(renderedOrAll[i].time).date.getTime() <= positionTime) {
          activeStatus = renderedOrAll[i];
          break;
        }
      }

      const representative =
        representativeByKey.get(folderKeyOf(activeStatus.name)) ?? activeStatus;
      const bucket = bucketByRepresentative.get(representative);
      if (bucket) {
        bucket.push(position);
      } else {
        bucketByRepresentative.set(representative, [position]);
      }
    });

    // Condense each bucket independently to ~1 ping per minute. The first ping
    // in the bucket is always retained, which is exactly the ping closest to
    // (and at-or-after) the bucket's status transition. Bucket insertion order
    // == chronological folder order (positions are sorted and activeStatus is
    // monotonic in positionTime), so the running concatenation below is the same
    // globally-chronological survivor list the map state and the (index, count)
    // "current position" heuristic in addLocationItemToFolder expect.
    const condensedBuckets: Array<[StatusFolder, PositionDto[]]> = [];
    let totalSurvivors = 0;
    bucketByRepresentative.forEach((bucket, representative) => {
      const condensed = removePositionsWithDuplicateMinutes(bucket);
      condensedBuckets.push([representative, condensed]);
      totalSurvivors += condensed.length;
    });

    const survivors: PositionDto[] = [];
    condensedBuckets.forEach(([representative, condensed]) => {
      condensed.forEach(position => {
        this.addLocationItemToFolder(
          representative,
          position,
          survivors.length,
          totalSurvivors,
          routeInfoFolderSourceKey,
        );
        survivors.push(position);
      });
    });

    return survivors;
  };

  private addLocationItemToFolder = (
    currentStatus: StatusFolder,
    item: PositionDto,
    index: number,
    count: number,
    // Callers in the status-processing chain pass the freshly resolved source key —
    // componentDidMount's setState may not have flushed yet when this runs.
    routeInfoFolderSourceKey: FolderListSource = this.state.routeInfoFolderSourceKey,
  ): void => {
    // `?? {}` — with an unresolved override there is no product line, hence no name map.
    const statusNameMap = retrieveProductLineNameDiff(this.effectiveProductLine!) ?? {};
    const textTime = StatusUtils.getReadableTimestamp(item.timestamp);
    const productLineFolder = this.getFolderListOfProductLine(routeInfoFolderSourceKey);
    const targetFolderId = determineWorkflowFolderKey(
      item.productLine,
      currentStatus.name,
      routeInfoFolderSourceKey,
    );
    const productLine = productLineFolder.find(
      folder => targetFolderId && folder.id === targetFolderId,
    );

    const itemObj: StatusItemData = {
      id: item.id.toString(),
      name: textTime,
      historical: true,
      color: productLine?.color,
      position: item,
    };

    currentStatus.itemList.push(itemObj);
    const positionStatus =
      statusNameMap[currentStatus.name as ProductLineStatusMapKeys] ?? constants.STATUS_IDS.UNKNOWN;

    item.status = currentStatus.name;
    item.historical = true;

    if (
      StatusUtils.shouldMarkAsCurrentPosition(
        positionStatus,
        statusNameMap,
        index,
        count,
        this.props.ticketTimeline,
      )
    ) {
      item.historical = false;
      StatusUtils.updateCurrentStatusItemHistorical(
        currentStatus,
        item.id.toString(),
        positionStatus,
        statusNameMap,
      );
    }
  };

  private getFolderList = (ticketStatuses?: StatusFolder[]) =>
    statusFolderList(ticketStatuses ?? [], this.getFolderListOfProductLine());

  private getFolderListOfProductLine = (
    source: FolderListSource = this.state.routeInfoFolderSourceKey,
  ): StatusFolder[] => {
    switch (this.effectiveProductLine) {
      case constants.READY_MIX_VALUE:
        return readyMixFolderList(STATUSES_STRING, source);
      case constants.CEMENT_VALUE:
        return cementFolderList(STATUSES_STRING, source);
      case constants.AGGREGATES_VALUE:
        return aggFolderList(STATUSES_STRING, source);
      default:
        return [];
    }
  };

  private setFoldersOpen = (updated: FoldersOpenState): void => {
    this.setState({ foldersOpen: updated });
  };

  private selectItem = (item: any): void => {
    const newSelectedItem = this.state.selectedItem === item.id ? null : item.id;

    if (newSelectedItem) {
      // Find the position for this item so we can center the map and show InfoPane
      const { position } = item;
      if (position) {
        this.updateSelectedItem(position);
        return;
      }
    }

    // If no position or deselecting, just clear selection
    this.setState({ selectedItem: newSelectedItem });
  };

  private updateSelectedItem = (position: PositionDto): void => {
    const statusNameMap = retrieveProductLineNameDiff(this.effectiveProductLine!) ?? {};
    const stringId = position.id.toString();

    const folderId = this.getFolderListOfProductLine().find(
      pl =>
        statusNameMap[pl.id as ProductLineStatusMapKeys] ===
        statusNameMap[position.status as ProductLineStatusMapKeys],
    )?.id;

    const nextFoldersOpen = folderId
      ? {
          ...this.state.foldersOpen,
          [folderId]: true,
        }
      : this.state.foldersOpen;

    this.setState({
      selectedItem: stringId,
      foldersOpen: nextFoldersOpen,
      mapCentered: true,
    });
  };

  private clearSelectedItem = (): void => {
    this.setState({ selectedItem: null });
  };

  private recenterMap = (): void => {
    this.setState({ mapCentered: true });
  };

  private getTicketClosedBy = (closed: boolean): JSX.Element | null => {
    if (this.effectiveProductLine !== constants.READY_MIX_VALUE) {
      return null;
    }

    const icon = closed ? 'icon-check-circle' : 'icon-more-horiz';
    const { driverClosed } = this.state;
    let message = TICKET_STATUS_OPEN;

    if (closed) {
      message = driverClosed ? TICKET_STATUS_CLOSED_DRIVER : TICKET_STATUS_CLOSED_SYSTEM;
    }

    return (
      <div className="margin-top-10">
        <div className="flex-vertical-center">
          <i className={cx(icon, styles.markerIcon, 'margin-right-10')} />
          <span>{message}</span>
        </div>
      </div>
    );
  };

  render(): JSX.Element {
    const { selectedTicket, plantList } = this.props;
    const startPlant = plantList.find(plant => plant.url === selectedTicket?.plant?.url);
    const isTicketClosed = !!selectedTicket?.closed;

    return (
      <div className={styles.ticketContentContainer}>
        <h2 className={styles.ticketRouteHeader}>Route Info</h2>
        <div className={styles.routeInfoContainer}>
          <Paper className={styles.routeInfoSidebar}>
            <div className="h5">
              {StatusUtils.getTitle('icon-home', 'Start Plant', startPlant?.name, '')}
              {StatusUtils.getTitle(
                'icon-flag',
                'Jobsite',
                selectedTicket?.order?.customerName,
                'margin-top-10',
              )}
              {this.getTicketClosedBy(isTicketClosed)}
            </div>
            <IconTitleUnderline
              className="margin-top-40"
              icon="icon-access-time"
              title="Status History"
            />
            <FolderList
              folderList={this.getFolderList(this.state.statuses)}
              onFolderClick={this.setFoldersOpen as any}
              onItemClick={this.selectItem}
              selectedItemId={this.state.selectedItem ?? undefined}
              openStateMap={this.state.foldersOpen as any}
              isFolderNameBold={false}
            />
          </Paper>
          {this.state.positions && (
            <div className={styles.mapContainer}>
              <RouteInfoMap
                statusFolders={this.getFolderList(this.state.statuses)}
                productLineFolders={this.getFolderListOfProductLine()}
                productLineOverride={this.props.productLineOverride}
                positionsList={this.state.positions}
                selectedItemId={this.state.selectedItem}
                updateSelectedItem={this.updateSelectedItem}
                clearSelectedItem={this.clearSelectedItem}
                recenterMap={this.recenterMap}
                mapCentered={this.state.mapCentered}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

// Simplified Redux connection (keeping original structure to minimize changes)
export default connect<any, any, any>(
  (state: any) => ({
    selectedTicket: state.selectedTicket,
    plantList: state.plantList,
    ticketTimeline: state.ticketTimeline,
    selectedProductLine: state.selectedProductLine,
    drivers: state.allDrivers,
  }),
  {
    openFailModal,
    fetchStatusList,
  },
)(RouteInfo);
